/**
 * Centralized error handling utilities
 * Provides safe error handling and sanitization
 */

import toast from 'react-hot-toast';
import { logger } from './logger';

export interface AppError {
  code?: string;
  message: string;
  status?: number;
  userMessage?: string;
  context?: Record<string, unknown>;
}

export class CustomError extends Error implements AppError {
  code?: string;
  status?: number;
  userMessage?: string;
  context?: Record<string, unknown>;

  constructor(
    message: string,
    options: {
      code?: string;
      status?: number;
      userMessage?: string;
      context?: Record<string, unknown>;
    } = {}
  ) {
    super(message);
    this.name = 'CustomError';
    this.code = options.code;
    this.status = options.status;
    this.userMessage = options.userMessage;
    this.context = options.context;
  }
}

/**
 * Sanitizes error data to remove sensitive information
 */
function sanitizeError(error: unknown): AppError {
  const sensitiveFields = [
    'password',
    'token',
    'refresh_token',
    'authorization',
    'secret',
    'key',
    'credential'
  ];

  const errorObj = error as {
    message?: string;
    status?: number;
    code?: string;
    context?: Record<string, unknown>;
    response?: { status?: number };
  };

  const sanitized: AppError = {
    message: errorObj?.message || 'An unexpected error occurred',
    status: errorObj?.status || errorObj?.response?.status,
    code: errorObj?.code
  };

  // Clean sensitive data from error context
  if (errorObj?.context) {
    const cleanContext = { ...errorObj.context };
    sensitiveFields.forEach(field => {
      Object.keys(cleanContext).forEach(key => {
        if (key.toLowerCase().includes(field)) {
          cleanContext[key] = '[REDACTED]';
        }
      });
    });
    sanitized.context = cleanContext;
  }

  return sanitized;
}

/**
 * Gets user-friendly error message based on error type and status
 */
function getUserFriendlyMessage(error: AppError): string {
  // If custom user message is provided, use it
  if (error?.message || error.userMessage) {
    return error?.message ?? error.userMessage;
  }

  // Handle common HTTP status codes
  switch (error.status) {
    case 400:
      return 'Invalid request. Please check your input and try again.';
    case 401:
      return 'Your session has expired. Please log in again.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource could not be found.';
    case 409:
      return 'A conflict occurred. This resource may already exist.';
    case 422:
      return 'The provided data is invalid. Please check and try again.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 500:
      return 'An internal server error occurred. Please try again later.';
    case 502:
    case 503:
    case 504:
      return 'The service is temporarily unavailable. Please try again later.';
    default:
      return error.message || 'An unexpected error occurred';
  }
}

/**
 * Global error handler for the application
 */
export function handleError(
  error: unknown,
  options: {
    showToast?: boolean;
    logError?: boolean;
    context?: Record<string, unknown>;
    fallbackMessage?: string;
  } = {}
): AppError {
  const {
    showToast = true,
    logError = true,
    context,
    fallbackMessage = 'An unexpected error occurred'
  } = options;

  // Sanitize the error
  const sanitizedError = sanitizeError(error);

  // Add additional context if provided
  if (context) {
    sanitizedError.context = { ...sanitizedError.context, ...context };
  }

  // Log the error (with sanitization)
  if (logError) {
    logger.error('Application error occurred', {
      message: sanitizedError.message,
      status: sanitizedError.status,
      code: sanitizedError.code,
      context: sanitizedError.context
    });
  }

  // Show user-friendly toast message
  if (showToast) {
    const userMessage =
      getUserFriendlyMessage(sanitizedError) || fallbackMessage;

    // Don't show duplicate toasts
    toast.dismiss();

    if (sanitizedError.status === 401) {
      toast.error(userMessage, { duration: 5000 });
    } else if (sanitizedError.status && sanitizedError.status >= 500) {
      toast.error(userMessage, { duration: 4000 });
    } else {
      toast.error(userMessage, { duration: 3000 });
    }
  }

  return sanitizedError;
}

/**
 * Creates a standardized error response
 */
export function createErrorResponse(
  message: string,
  status: number = 500,
  code?: string,
  userMessage?: string
): AppError {
  return {
    message,
    status,
    code,
    userMessage: userMessage || getUserFriendlyMessage({ message, status })
  };
}

/**
 * Handles async operations with error catching
 */
export async function safeAsync<T>(
  operation: () => Promise<T>,
  errorContext?: string
): Promise<{ data?: T; error?: AppError }> {
  try {
    const data = await operation();
    return { data };
  } catch (error) {
    const handledError = handleError(error, {
      context: errorContext ? { operation: errorContext } : undefined
    });
    return { error: handledError };
  }
}

/**
 * Validates that a response is successful
 */
export function validateResponse(response: {
  status?: number;
  message?: string;
  code?: string;
  success?: boolean;
}): void {
  if (!response) {
    throw new CustomError('No response received', {
      status: 500,
      userMessage: 'Failed to get response from server'
    });
  }

  if (response.status && response.status >= 400) {
    throw new CustomError(response.message || 'Request failed', {
      status: response.status,
      code: response.code
    });
  }

  if (response.success === false) {
    throw new CustomError(response.message || 'Operation failed', {
      status: response.status || 400,
      code: response.code
    });
  }
}
